import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Divider, InputNumber } from "antd";
import { Info } from 'lucide-react';

import Form, { FormInputComponent } from "../../components/form/Form";
import { useOpportunity } from "../../providers/OpportunityProvider";
import SpinLoader from "../../components/SpinLoader";
import { CalendarOutlined, ClockCircleOutlined, DollarOutlined, RiseOutlined, WarningOutlined } from "@ant-design/icons";


const FixedRetainerPayoutEditTab = ({
    onUpdate = async () => true,
    showSteps,
    getStepActions = false, ...props
}) => {
    const { opportunity, isDisabled, changeProps, isUpdating, onRequest } = useOpportunity();
    const formRef = useRef(null);

    let talentOpp = TB.cloneDeep(opportunity.job_candidate.talent_opps[0]);

    const editable = opportunity.canEditProposal();
    const frequencyType = opportunity.project_position.payout_frequency === 'weekly' ? 'weeks' : 'months'
    // const [frequencyType, setFrequencyType] = useState('weeks');
    const totalPeriods = opportunity.project_position.actual_total_periods;
    const [isSettingDefault, setIsSettingDefault] = useState(false);
    const defaultPayoutPerPeriod = useCallback(
        () => {
            return Math.round(parseFloat(Number(opportunity.project_position?.payout_amount).toFixed(2)));
        },
        [opportunity.project_position?.payout_amount]
    );

    const defaultPayoutRateBased = useCallback(() => {
            return Math.round(parseFloat(Number(opportunity.project_position?.estimated_rate).toFixed(2)));
        }, [opportunity.project_position?.estimated_rate]
    );

    const [formModel, setFormModel] = useState({
        ...TB.pick({
            ...talentOpp
        }, [
            'hourly_rate',
            'position_deliverables',
            'has_accepted_proposed_payout',
        ]),
        payout_amount: talentOpp.payout_amount || null,
        confirmed_availability: true,
        confirmed_avg_hours_per_period: opportunity.job_candidate?.confirmed_avg_hours_per_period || opportunity.project_position.avg_hours_per_period || null,
        hourly_rate: talentOpp?.hourly_rate || opportunity.project_position.estimated_rate,
    });

    // console.log(formModel, 'formModel')
    // console.log('opportunity', opportunity)

    const resetForm = async () => {
        return formRef.current?.forceReset(talentOpp)
    }

    // const isFixedRetainer = opportunity.project_position.isFixedRetainer();
    const isCostBased = opportunity.project_position.isFixedRetainerCostPerPeriodBased();
    const isRateBased = opportunity.project_position.isFixedRetainerRateBased();

    // Demo toggle for proposal type
    const proposalType = isCostBased ? 'simple' : 'hourly'
    // const [proposalType, setProposalType] = useState('simple'); // 'simple' or 'hourly'

    const submitForm = async () => {
        console.log('submitting form')
        let invalid = formRef.current.forceValidate(formModel);
        if (invalid) {
            console.log('invalid', invalid)
            return;
        }

        const body = {}
        if (isCostBased) {
            body.payout_amount = formModel.payout_amount;
            body.job_candidate = {
                confirmed_avg_hours_per_period: null,
                hourly_rate: null,
            }
        }

        if (isRateBased) {
            body.payout_amount = formModel.confirmed_avg_hours_per_period * formModel.hourly_rate;
            body.job_candidate = {
                confirmed_avg_hours_per_period: formModel.confirmed_avg_hours_per_period,
                hourly_rate: formModel.hourly_rate,
            }
        }
        console.log(body, 'body on submit')
        let success = await onUpdate(body);
        if (success) {
            await resetForm();
        }
    }

    const projectBudget = useMemo(() => {
        return Math.ceil(parseInt(opportunity.project_position.payout_amount * opportunity.project_position.actual_total_periods))
    }, [
        opportunity.project_position?.payout_amount,
        opportunity.project_position?.actual_total_periods
    ]);


    // Default hours based on frequency type
    const defaultHoursPerPeriod = frequencyType === 'weeks' ? 40 : 160;

    // Calculate default values
    const estimatedPayoutPerPeriod = projectBudget / totalPeriods;
    const defaultHourlyRate = projectBudget / totalPeriods / defaultHoursPerPeriod;

    // Calculated values
    const proposedPayoutPerPeriod = useMemo(() => {
        return isRateBased ? (
            (formModel.hourly_rate * formModel.confirmed_avg_hours_per_period)) :
            formModel.payout_amount
    }, [
        formModel.hourly_rate,
        formModel.confirmed_avg_hours_per_period,
        formModel.payout_amount,
        opportunity.project_position?.actual_total_periods,
    ]);
    const totalProposedPayout = useMemo(() => {
        return proposedPayoutPerPeriod * totalPeriods
    }, [proposedPayoutPerPeriod, totalPeriods]);
    const isOverBudget = Math.round(totalProposedPayout) > projectBudget;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatHourlyRate = (rate) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(rate);
    };

    const percentageOfBudget = ((totalProposedPayout / projectBudget) * 100).toFixed(1);
    const totalHours = formModel.confirmed_avg_hours_per_period * totalPeriods;

    // const isFixedFeeType = opportunity.isFixedFee();

    const fixedRetainerCopy = (
        <span>
            The client has request a <b>fixed {opportunity.project_position.payout_frequency} retainer</b> contract for
            this assignment, meaning you will be paid a fixed dollar amount on
            a {opportunity.project_position.payout_frequency} basis per specific activities and deliverables required
            for this assignment.
        </span>
    )

    return (
        <SpinLoader isLoading={isUpdating}>
            <h2 className="text-gray-900 text-xl pb-4">
                Type of Contract: <span className="text-danger">Fixed Retainer
                    ({TB.capitalize(opportunity.project_position.payout_frequency)})</span>
            </h2>

            {getStepActions && getStepActions(
                // 'Please confirm your 1) bill rate, 2) hours per deliverable and 3) payout to work on this project',
                // 'Please confirm your total proposed cost for this project',
                fixedRetainerCopy,
                submitForm
            )}
            <div className="opp-talent-proposal-form">

                <Form
                    ref={formRef}
                    background={"transparent"}
                    // mapping={mapping}
                    minimal={true}
                    submitButton={false}
                    isDisabled={isDisabled}
                    onSubmit={submitForm}
                    model={formModel}
                    fields={[]}
                    onChange={async (model) => {
                        // let has_accepted_proposed_payout = formModel.has_accepted_proposed_payout;
                        let updatedModel = { ...formModel }
                        for (let [key, value] of Object.entries(model)) {
                            updatedModel = TB.set(updatedModel, key, value);
                        }
                        await setFormModel(updatedModel);
                    }}
                >

                    <div className="relative">

                        <div className="flex flex-col gap-6 justify-between relative mb-6">
                            <div className="flex flex-col gap-2 max-w-4xl">
                                {!getStepActions && (
                                    <p className="text-gray-600 inline">
                                        {fixedRetainerCopy}
                                    </p>
                                )}
                            </div>

                            <div
                                className="self-stretch max-w-3xl bg-gray-50 px-6 pt-3 pb-6 mb-6 flex flex-col items-stretch gap-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                    <DollarOutlined className="mr-2 text-green-600" style={{ fontSize: '20px' }} />
                                    Project Overview
                                </h3>
                                <div className="flex flex-wrap md:flex-nowrap justify-between gap-8">

                                    <div className="text-center min-w-28">
                                        <div
                                            className="text-lg font-medium text-green-600 whitespace-nowrap">{formatCurrency(projectBudget)}</div>
                                        <div className="text-xs text-gray-600 whitespace-nowrap">est. project budget
                                        </div>
                                    </div>


                                    <div className="text-center min-w-28">
                                        <div
                                            className="text-lg font-medium text-black whitespace-nowrap">{formatCurrency(defaultPayoutPerPeriod())}</div>
                                        <div className="text-xs text-gray-600 whitespace-nowrap">
                                            est. payout per {frequencyType.slice(0, -1)}
                                        </div>
                                    </div>

                                    <div className="text-center min-w-28">
                                        <div
                                            className="text-lg font-medium whitespace-nowrap">{TB.moment(opportunity.project_position.started_at).format('ll')}</div>
                                        <div className="text-xs text-gray-600 whitespace-nowrap">est. start date</div>
                                    </div>

                                    <div className="text-center min-w-28">
                                        <div
                                            className="text-lg font-medium text-blue-600 flex flex-nowrap items-center justify-center whitespace-nowrap">
                                            <CalendarOutlined className="mr-2" style={{ fontSize: '20px' }} />
                                            {totalPeriods}
                                        </div>
                                        <div className="text-xs text-gray-600 whitespace-nowrap">{frequencyType}</div>
                                    </div>
                                    {/*<div className="text-center">*/}
                                    {/*    <div className="text-lg font-medium">{formatCurrency(estimatedPayoutPerPeriod)}</div>*/}
                                    {/*    <div className="text-xs text-gray-600 whitespace-nowrap">est. per {frequencyType.slice(0, -1)}</div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>
                        </div>

                        <div className="mb-6 flex flex-col items-stretch divide-y divide-gray-300">

                            <p className="text-lg font-bold py-2">Role Activities</p>

                            {!opportunity.project_position?.deliverables?.length ? (
                                <div className="pt-4">
                                    <p className="text-center">No Activities Given</p>
                                </div>
                            ) : (
                                TB.map(opportunity.project_position?.deliverables, (deliverable, delIndex) => <div
                                    className="flex flex-row items-center py-2.5 gap-5" key={`del-${deliverable.id}`}>

                                    <div className="flex flex-row items-start gap-4 flex-grow">
                                        <i className="fas text-[24px] fa-clipboard-check" />
                                        <div className="flex flex-col gap-2 items-start flex-grow">
                                            <p className="font-medium text-base">
                                                {deliverable.name}
                                            </p>
                                            <p className="text-sm">
                                                {deliverable.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>)
                            )}

                        </div>
                        <Divider />

                        {/* Conditional Explanation */}
                        {/*{proposalType === 'hourly' && (*/}
                        {/*    <div className="max-w-2xl mt-3 mb-6 bg-purple-50 border border-purple-200 p-4">*/}
                        {/*        <div className="flex items-start">*/}
                        {/*            <Info className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />*/}
                        {/*            <div>*/}
                        {/*                <p className="text-base text-purple-800 font-semibold mb-1">Hourly Breakdown Required</p>*/}
                        {/*                <p className="text-purple-700 text-xs">*/}
                        {/*                    {`The client has requested a detailed hourly breakdown to better understand your time commitment and availability for this project. Based upon an estimated hours per ${frequencyType.slice(0, -1)}, please specify your preferred hourly rate.`}*/}
                        {/*                </p>*/}
                        {/*            </div>*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*)}*/}
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            My Proposal
                        </h3>

                        <div className="flex flex-col gap-2 max-w-2xl p-6 mb-6 border-2 bg-blue-50 border-blue-100">
                            {/* Dynamic Input Section */}
                            {proposalType === 'simple' ? (
                                // Simple Payout Input
                                <div>
                                    <label className="block font-medium text-gray-900 mb-3">
                                        Proposed Payout
                                        per {frequencyType.charAt(0).toUpperCase() + frequencyType.slice(1, -1)}
                                    </label>
                                    <div className="flex flex-row gap-3 items-start">
                                        <FormInputComponent
                                            fieldName="payout_amount"
                                            label={false}
                                            type="antd-input"
                                            className="flex-grow"
                                            value={formModel.payout_amount}
                                            placeholder={defaultPayoutPerPeriod()}
                                        >
                                            <InputNumber
                                                className="w-full text-lg font-medium"
                                                prefix="$"
                                                size="large"
                                                step={1}
                                                controls={false}
                                                precision={2}
                                            />

                                        </FormInputComponent>
                                        <Button type="primary" htmlType="button" className="h-[38px]"
                                            loading={isSettingDefault}
                                            onClick={async () => {
                                                await setIsSettingDefault(true)
                                                await TB.wait(500);
                                                await setFormModel({
                                                    ...formModel,
                                                    payout_amount: defaultPayoutPerPeriod()
                                                });
                                                await setIsSettingDefault(false)
                                            }}
                                            disabled={isDisabled || Math.round(formModel.payout_amount) === Math.round(defaultPayoutPerPeriod())}>
                                            Set to Client Budget
                                            ({TB.formatCurrency(defaultPayoutPerPeriod(), false)} per {frequencyType.slice(0, -1)})
                                        </Button>
                                    </div>

                                </div>
                            ) : (
                                // Hourly Breakdown Inputs
                                <div className="flex flex-wrap gap-6">
                                    <div className="flex-1 min-w-64">
                                        <label className="block font-medium text-gray-900 mb-3">
                                            Requested Availability
                                        </label>

                                        <p className="w-full text-lg font-medium text-gray-900 leading-[39.6px]">
                                            <span
                                                className="text-secondary font-semibold">{opportunity.project_position.avg_hours_per_period} </span>
                                            hours per {frequencyType.slice(0, -1)}
                                        </p>
                                    </div>

                                    <div className="flex-1 min-w-64">
                                        <label className="block font-medium text-gray-900 mb3">
                                            Hourly Rate
                                        </label>
                                        <FormInputComponent
                                            fieldName="hourly_rate"
                                            label={false}
                                            type="antd-input"
                                            value={formModel.hourly_rate}
                                            placeholder={opportunity.project_position.estimated_rate}
                                            className="mb-0"
                                        >
                                            <InputNumber
                                                className="w-full text-lg font-medium"
                                                prefix="$"
                                                step={1}
                                                controls={false}
                                                precision={2}
                                                size="large" />
                                        </FormInputComponent>

                                        {proposalType === 'hourly' && (
                                            <p className="text-sm mt-1.5 capitalize pl-3">{formatCurrency(proposedPayoutPerPeriod)} {opportunity.project_position.payout_frequency} Retainer</p>
                                        )}

                                        <Button
                                            type="primary"
                                            htmlType="button"
                                            className="h-[38px] my-4"
                                            loading={isSettingDefault}
                                            onClick={async () => {
                                                await setIsSettingDefault(true)
                                                await TB.wait(500);
                                                await setFormModel({
                                                    ...formModel,
                                                    hourly_rate: defaultPayoutRateBased()
                                                });
                                                await setIsSettingDefault(false)
                                            }}
                                            disabled={isDisabled || Math.round(formModel.hourly_rate) === Math.round(defaultPayoutRateBased())}
                                        >
                                            Set to Client Budget
                                            ({TB.formatCurrency(defaultPayoutRateBased(), false)} per hour)
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 border-t border-gray-200">
                                <div className="text-sm text-gray-600 space-y-1">
                                    <div>Payment
                                        schedule: <b>{formatCurrency(proposedPayoutPerPeriod)}</b> × <b>{totalPeriods} {frequencyType}</b> = <b>{formatCurrency(totalProposedPayout)}</b>
                                    </div>
                                    {proposalType === 'hourly' && (
                                        <>
                                            <div>Total time commitment: <b>{totalHours}</b> hours
                                                over <b>{totalPeriods} {frequencyType}</b></div>
                                            <div>Effective hourly rate: <b>{formatHourlyRate(formModel.hourly_rate)}</b>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* Proposal Summary */}
                        <div className="max-w-2xl p-6 mb-6 border-2 bg-blue-50 border-blue-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <RiseOutlined className="mr-2 text-blue-600" style={{ fontSize: '20px' }} />
                                Your Proposal Summary
                            </h3>

                            <div className="flex flex-wrap justify-between gap-6 mb-4">
                                <div className="text-center">
                                    <div className="text-sm text-gray-600 mb-1">Payout per {frequencyType.slice(0, -1)}</div>
                                    <div
                                        className="text-2xl font-bold text-gray-900">{formatCurrency(proposedPayoutPerPeriod)}</div>
                                    {proposalType === 'hourly' && (
                                        <div className="text-xs text-gray-500">
                                            {formModel.confirmed_avg_hours_per_period}hrs
                                            × {formatHourlyRate(formModel.hourly_rate)}
                                        </div>
                                    )}
                                </div>
                                <div className="text-center">
                                    <div className="text-sm text-gray-600 mb-1">Total proposed payout</div>
                                    <div className={`text-2xl font-bold text-green-600`}>
                                        {formatCurrency(totalProposedPayout)}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        <b>{percentageOfBudget}%</b> of project budget
                                    </div>
                                </div>
                            </div>


                        </div>

                        {/* Warning for over-budget */}
                        {isOverBudget && (
                            <Alert
                                message="Proposal exceeds budget"
                                description={
                                    `Your total proposed payout of ${formatCurrency(totalProposedPayout)} exceeds the estimated project budget by ${formatCurrency(totalProposedPayout - projectBudget)}. ${proposalType === 'simple'
                                        ? `Consider adjusting your per-${frequencyType.slice(0, -1)} amount to align with the budget.`
                                        : `Consider adjusting your hours per ${frequencyType.slice(0, -1)} or hourly rate to align with the budget.`
                                    }`
                                }
                                type="warning"
                                showIcon
                                icon={<WarningOutlined />}
                                className="mb-6 max-w-2xl"
                            />
                        )}

                    </div>

                    <div className="pt-3">
                        {editable && !showSteps && (
                            <div className="mt-3 flex flex-row justify-end">
                                <button type="submit" className="btn btn-primary mr-3"
                                    // disabled={isDisabled || !hasFormModelChanged}>
                                    disabled={isDisabled}
                                >
                                    Save
                                </button>
                                <button className="btn btn-outline-dark"
                                    type="button" onClick={resetForm}
                                    // disabled={isDisabled || !hasFormModelChanged}>
                                    disabled={isDisabled}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}

                    </div>
                </Form>

            </div>
        </SpinLoader>
    )
};

export default FixedRetainerPayoutEditTab;